import { useCallback, useEffect, useMemo, useState } from "react";

import { BottomNav } from "@/components/cockpit/BottomNav";
import { CaseCard } from "@/components/cockpit/CaseCard";
import { Sidebar } from "@/components/cockpit/Sidebar";
import { VitalSignsRibbon, type VitalSigns } from "@/components/cockpit/VitalSignsRibbon";
import {
  type EmailCardData,
  getEmails,
  getTasks,
  type CockpitTask,
} from "@/lib/emails";

const REFRESH_INTERVAL_MS = 60_000;

interface State {
  emails: EmailCardData[];
  tasks: CockpitTask[];
  loading: boolean;
  error: string | null;
  fyiExpanded: boolean;
  doneExpanded: boolean;
}

/** Triage lane board (brief §9, reference image 2).
 *
 *  Three lanes — NEEDS YOU / WAITING ON OTHERS / TASKS — with two
 *  collapsed bottom strips for DONE TODAY and FYI / AUTO-HANDLED.
 *  The brief is explicit that FYI must always be one click away,
 *  not permanently buried. */
export function LaneBoard() {
  const [state, setState] = useState<State>({
    emails: [],
    tasks: [],
    loading: true,
    error: null,
    fyiExpanded: false,
    doneExpanded: false,
  });
  const [signingOut, setSigningOut] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [emailsRes, tasksRes] = await Promise.all([
        getEmails(),
        getTasks("open"),
      ]);
      const allEmails = [
        ...emailsRes.buckets.need_reply_now,
        ...emailsRes.buckets.awaiting_your_action,
        ...emailsRes.buckets.waiting_on_client,
        ...emailsRes.buckets.auto_handled_fyi,
      ];
      setState((prev) => ({
        ...prev,
        emails: allEmails,
        tasks: tasksRes.tasks,
        loading: false,
        error: null,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void refresh();
    const interval = window.setInterval(() => {
      if (!cancelled) void refresh();
    }, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [refresh]);

  // Group emails by lane semantics. Note: NEEDS_YOU is the union of
  // need_reply_now + awaiting_your_action; WAITING_ON_OTHERS maps to
  // waiting_on_client; the FYI strip catches auto_handled_fyi.
  const { needsYou, waitingOnOthers, fyi, done } = useMemo(() => {
    const ny: EmailCardData[] = [];
    const wo: EmailCardData[] = [];
    const fy: EmailCardData[] = [];
    const dn: EmailCardData[] = [];
    for (const e of state.emails) {
      if (e.triage_state === "DONE") dn.push(e);
      else if (e.lane === "FYI") fy.push(e);
      else if (e.lane === "WAITING_ON_OTHERS") wo.push(e);
      else ny.push(e); // NEEDS_YOU + anything malformed defaults here
    }
    return { needsYou: ny, waitingOnOthers: wo, fyi: fy, done: dn };
  }, [state.emails]);

  const vitals: VitalSigns = useMemo(() => {
    const overdue = needsYou.filter(
      (e) =>
        e.received_at &&
        (Date.now() - new Date(e.received_at).getTime()) / 36e5 > 24 &&
        e.bucket === "need_reply_now",
    ).length;
    return {
      sla_label: "live",
      queue_depth: needsYou.length + waitingOnOthers.length,
      overdue,
      due_today: needsYou.filter(
        (e) =>
          e.received_at &&
          (Date.now() - new Date(e.received_at).getTime()) / 36e5 < 12,
      ).length,
      escalation_level: needsYou.some((e) => e.escalation_signal)
        ? "critical"
        : needsYou.some((e) => e.priority === "P0")
          ? "active"
          : "ok",
    };
  }, [needsYou, waitingOnOthers]);

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await fetch("/auth/logout", { method: "POST", credentials: "include" });
    } catch {
      /* still bounce */
    }
    window.location.href = "/login";
  }, []);

  return (
    <main className="flex min-h-screen bg-op-base text-op-primary">
      <Sidebar
        items={[
          { to: "/", label: "Cockpit", glyph: "◎" },
          { to: "/lanes", label: "Lanes", glyph: "▤" },
          { to: "/tasks", label: "Tasks", glyph: "☑", badge: state.tasks.length },
          { to: "/digest", label: "Digest", glyph: "△", badge: vitals.overdue },
          { to: "/dashboard", label: "Show everything", glyph: "⊞" },
        ]}
        onSignOut={handleSignOut}
        signingOut={signingOut}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <VitalSignsRibbon vitals={vitals} />

        <div className="flex-1 overflow-auto px-4 py-4 md:px-6">
          {state.error ? (
            <div
              role="alert"
              data-testid="lanes-error"
              className="mb-3 rounded border border-op-border bg-op-elevated px-3 py-2 text-xs text-op-secondary"
            >
              {state.error}
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-3" data-testid="lane-board-grid">
            <Lane
              testId="lane-needs-you"
              title="Needs you"
              accent="bg-bucket-need-reply-now"
              emails={needsYou}
            />
            <Lane
              testId="lane-waiting-on-others"
              title="Waiting on others"
              accent="bg-op-accent"
              emails={waitingOnOthers}
            />
            <TasksLane tasks={state.tasks} />
          </div>

          <CollapsibleStrip
            testId="lane-board-done"
            title="Done today"
            count={done.length}
            expanded={state.doneExpanded}
            onToggle={() =>
              setState((prev) => ({ ...prev, doneExpanded: !prev.doneExpanded }))
            }
          >
            {done.length === 0 ? (
              <p className="text-xs text-op-muted">Nothing closed yet today.</p>
            ) : (
              <div className="space-y-2">
                {done.map((e) => (
                  <CaseCard key={e.id} email={e} variant="compact" />
                ))}
              </div>
            )}
          </CollapsibleStrip>

          <CollapsibleStrip
            testId="lane-board-fyi"
            title="FYI / auto-handled"
            count={fyi.length}
            expanded={state.fyiExpanded}
            onToggle={() =>
              setState((prev) => ({ ...prev, fyiExpanded: !prev.fyiExpanded }))
            }
          >
            {fyi.length === 0 ? (
              <p className="text-xs text-op-muted">
                Nothing auto-handled in the last 7 days.
              </p>
            ) : (
              <div className="space-y-2">
                {fyi.map((e) => (
                  <CaseCard key={e.id} email={e} variant="compact" />
                ))}
              </div>
            )}
          </CollapsibleStrip>
        </div>

        <BottomNav
          items={[
            { to: "/", label: "Cockpit", glyph: "◎" },
            { to: "/lanes", label: "Lanes", glyph: "▤" },
            { to: "/tasks", label: "Tasks", glyph: "☑", badge: state.tasks.length },
            { to: "/digest", label: "Esc.", glyph: "△", badge: vitals.overdue },
          ]}
        />
      </div>
    </main>
  );
}

function Lane({
  title,
  emails,
  testId,
  accent,
}: {
  title: string;
  emails: EmailCardData[];
  testId: string;
  accent: string;
}) {
  return (
    <section
      data-testid={testId}
      className="flex min-w-0 flex-col gap-3 rounded border border-op-border bg-op-surface px-3 py-3"
    >
      <header className="flex items-center justify-between border-b border-op-border pb-2">
        <div className="flex items-center gap-2">
          <span className={`inline-block h-2 w-2 rounded-full ${accent}`} />
          <h2 className="text-[10px] font-semibold uppercase tracking-wider text-op-primary">
            {title}
          </h2>
        </div>
        <span className="rounded-full bg-op-elevated px-2 py-0.5 font-mono text-[11px] text-op-secondary">
          {emails.length}
        </span>
      </header>
      <div className="flex flex-col gap-2">
        {emails.length === 0 ? (
          <p className="rounded border border-dashed border-op-border px-3 py-6 text-center text-xs text-op-muted">
            Nothing here.
          </p>
        ) : (
          emails.map((e) => <CaseCard key={e.id} email={e} variant="compact" />)
        )}
      </div>
    </section>
  );
}

function TasksLane({ tasks }: { tasks: CockpitTask[] }) {
  return (
    <section
      data-testid="lane-tasks"
      className="flex min-w-0 flex-col gap-3 rounded border border-op-border bg-op-surface px-3 py-3"
    >
      <header className="flex items-center justify-between border-b border-op-border pb-2">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-op-amber" />
          <h2 className="text-[10px] font-semibold uppercase tracking-wider text-op-primary">
            Tasks
          </h2>
        </div>
        <span className="rounded-full bg-op-elevated px-2 py-0.5 font-mono text-[11px] text-op-secondary">
          {tasks.length}
        </span>
      </header>
      <div className="flex flex-col gap-2">
        {tasks.length === 0 ? (
          <p className="rounded border border-dashed border-op-border px-3 py-6 text-center text-xs text-op-muted">
            No open tasks.
          </p>
        ) : (
          tasks.slice(0, 12).map((t) => (
            <article
              key={t.id}
              data-testid="lane-tasks-row"
              className="rounded border border-op-border bg-op-base px-3 py-2"
            >
              <p
                className="truncate text-sm text-op-primary"
                title={t.title}
              >
                {t.title}
              </p>
              <p className="mt-1 flex flex-wrap items-center gap-x-3 text-[11px] text-op-secondary">
                {t.owner ? (
                  <span className="font-mono uppercase tracking-wider">
                    {t.owner}
                  </span>
                ) : null}
                {t.due_at ? (
                  <span className="font-mono">
                    due {new Date(t.due_at).toLocaleDateString()}
                  </span>
                ) : null}
              </p>
            </article>
          ))
        )}
      </div>
    </section>
  );
}

function CollapsibleStrip({
  title,
  count,
  expanded,
  onToggle,
  children,
  testId,
}: {
  title: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <section
      data-testid={testId}
      data-expanded={expanded ? "true" : "false"}
      className="mt-4 rounded border border-op-border bg-op-surface"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between px-3 py-2 text-left text-[10px] uppercase tracking-wider text-op-secondary hover:bg-op-elevated"
      >
        <span>{title}</span>
        <span className="rounded-full bg-op-elevated px-2 py-0.5 font-mono text-[11px] text-op-secondary">
          {count}
        </span>
      </button>
      {expanded ? <div className="border-t border-op-border px-3 py-3">{children}</div> : null}
    </section>
  );
}
